import { useEffect, useState, useCallback } from "react";
import {
  getSubscriptionStatus,
  getSubscriptionUsage,
} from "~src/api/subscription";
import { NoSubscriptionPopup } from "~src/components/popup/NoSubscriptionPopup";
import useAuth from "~src/hooks/useAuth";
import { createAbortController } from "~src/api/common/api";
import { logError, logWarning } from "~src/utils/logger";
import { UserRole } from "~src/types/auth";

// Helper function to detect canceled errors
const isCanceledError = (error: any): boolean => {
  if (!error) return false;
  
  // Check common patterns for canceled requests
  if (error.name === 'CanceledError') return true;
  if (error.code === 'ERR_CANCELED') return true;
  if (error.message?.includes('canceled')) return true;
  if (error.message?.includes('aborted')) return true;
  
  return false;
};

// Check subscription status every 5 minutes
const SUBSCRIPTION_CHECK_INTERVAL = 5 * 60 * 1000;
// After portal return, poll every 2 seconds for up to 30 seconds
const PORTAL_POLL_INTERVAL = 2 * 1000;
const PORTAL_MAX_POLLS = 15; // 30 seconds total (15 polls * 2 seconds)

export function SubscriptionCheck() {
  const { user, isAuthenticated } = useAuth();
  const [subscriptionPopup, setSubscriptionPopup] = useState(false);

  const checkSubscriptionStatus = useCallback(async (signal?: AbortSignal) => {
    try {
      // Check subscription status
      const subscriptionStatus = await getSubscriptionStatus({ signal });

      if (!signal?.aborted) {
        // If subscription is not active, show popup
        if (!subscriptionStatus.active_subscription) {
          setSubscriptionPopup(true);
          return;
        }

        // Subscription is active - hide popup immediately
        // The /active endpoint is the authoritative source for subscription status
        setSubscriptionPopup(false);

        // Optionally check usage data for logging purposes only
        // Usage data may not be immediately available for new users
        try {
          const subscriptionUsage = await getSubscriptionUsage({ signal });
          if (!signal?.aborted && subscriptionUsage?.message) {
            // Usage data not yet available - this is normal for new users
            logWarning(`Usage data not yet available: ${subscriptionUsage.message}`);
          }
        } catch (error) {
          // Skip logging for canceled requests
          if (!signal?.aborted && !isCanceledError(error)) {
            logWarning(`Error checking subscription usage: ${error}`);
            // Don't show popup - we already confirmed subscription is active above
          }
        }
      }
    } catch (error) {
      // Skip logging for canceled requests
      if (!signal?.aborted && !isCanceledError(error)) {
        logError(`Error checking subscription status: ${error}`);
        // Show popup on error to be safe
        setSubscriptionPopup(true);
      }
    }
  }, []);

  useEffect(() => {
    // Only proceed if user is authenticated and not an admin
    if (!isAuthenticated || !user || user.role === UserRole.ADMIN || user.is_superuser) {
      return;
    }

    // Create an abort controller for this effect instance
    const { signal, cleanup } = createAbortController();
    let portalPollInterval: NodeJS.Timeout | null = null;
    let normalIntervalId: NodeJS.Timeout | null = null;

    // Check if user just returned from portal (same flag used by BillingInfo)
    const isPortalReturn = sessionStorage.getItem('portal_return') === 'true';

    // Add a small delay before checking subscription to allow login to complete
    const initialCheckTimeout = setTimeout(() => {
      checkSubscriptionStatus(signal);
    }, 1000); // 1 second delay

    // If user returned from portal, set up aggressive polling
    if (isPortalReturn) {
      let pollCount = 0;

      portalPollInterval = setInterval(() => {
        pollCount++;
        if (pollCount >= PORTAL_MAX_POLLS || signal.aborted) {
          // Stop portal polling after max polls or if aborted
          if (portalPollInterval) {
            clearInterval(portalPollInterval);
            portalPollInterval = null;
          }
          // Note: Don't remove flag here - BillingInfo.tsx manages this flag

          // Start normal interval polling if not aborted
          if (!signal.aborted) {
            normalIntervalId = setInterval(() => {
              checkSubscriptionStatus(signal);
            }, SUBSCRIPTION_CHECK_INTERVAL);
          }
        } else {
          checkSubscriptionStatus(signal);
        }
      }, PORTAL_POLL_INTERVAL);
    } else {
      // Set up normal periodic checks
      normalIntervalId = setInterval(() => {
        checkSubscriptionStatus(signal);
      }, SUBSCRIPTION_CHECK_INTERVAL);
    }

    // Clean up function to abort any pending requests and clear intervals
    return () => {
      clearTimeout(initialCheckTimeout);
      cleanup();
      if (portalPollInterval) {
        clearInterval(portalPollInterval);
      }
      if (normalIntervalId) {
        clearInterval(normalIntervalId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, isAuthenticated, checkSubscriptionStatus]);
  
  return <NoSubscriptionPopup open={subscriptionPopup} />;
}
